import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Else, If, Then } from 'react-if';
import { useModal } from '@ebay/nice-modal-react';
import { DebounceClick, useDidUpdate, usePreservedCallback } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { Button } from '@zep/ui';
import { useRegisterPaymentCard } from '../../../../../services';
import { paymentFailureFromError } from '../../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog } from '../../../payment-tracking/PaymentFailureContent';
import { isPaymentCancellation } from '../../../payment-tracking/paymentFailureDecision';
import { useGoSignin } from '../../hooks/useGoSignin';
import { RegisterPaymentSchemaType } from '../../paymentForm';
import { useSubscriptionPaymentContext, useSubscriptionPaymentLog } from '../hooks';
import { findSubscriptionError, usePaymentAction } from '../hooks/usePaymentAction';
import { PaymentRetryDialog } from '../modals/PaymentRetryDialog';
export const PaymentSubmit = ({
  isAgree
}: {
  isAgree?: boolean;
}) => {
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['subscription']);
  const moveSignIn = useGoSignin();
  const paymentRetryDialog = useModal(PaymentRetryDialog);
  const {
    spaceId,
    spaceHashId,
    isUpdated,
    billingInfo,
    isUpdatePayment,
    isSubmitLoading,
    amplitudeData,
    handleIsRegisterCard,
    isRegisterCard,
    isAnnuallyPayment
  } = useSubscriptionPaymentContext();
  const hasRegisteredCard = useRef<boolean | undefined>(billingInfo === undefined ? undefined : billingInfo !== null);
  if (hasRegisteredCard.current === undefined && billingInfo !== undefined) {
    hasRegisteredCard.current = billingInfo !== null;
  }
  const isOnlySubscribe = !isUpdatePayment && isUpdated;
  const send = useSubscriptionPaymentLog('payment_subscription_purchase', false, isAnnuallyPayment ? 'Annual' : 'Monthly');
  const {
    handleSubmit,
    getValues,
    formState: {
      errors,
      isDirty
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  const {
    mutateAsync: _registerPaymentCard
  } = useRegisterPaymentCard();
  const {
    subscribePlan: _subscribePlan
  } = usePaymentAction();
  const registerPaymentCard = async () => {
    const {
      name: cardName,
      cardNumber,
      businessNumber,
      isBusiness,
      expireDate,
      birthDate
    } = getValues();
    const paymentFlow = hasRegisteredCard.current === undefined ? undefined : hasRegisteredCard.current ? 'card_change' as const : 'card_registration' as const;
    handleIsRegisterCard(false);
    // 결제하기 시도시에 하기로합의
    send(amplitudeData);
    try {
      await _registerPaymentCard({
        spaceHashId,
        paymentFlow,
        cardExpirationMonth: expireDate.substring(0, 2),
        cardExpirationYear: expireDate.substring(2, 4),
        customerIdentityNumber: isBusiness ? businessNumber : birthDate,
        cardNumber,
        cardName
      });
      hasRegisteredCard.current = true;
      handleIsRegisterCard(true);
    } catch (e) {
      const {
        message,
        type
      } = findSubscriptionError(e);
      const failure = paymentFlow ? {
        payment_flow: paymentFlow,
        pg_provider: 'toss' as const,
        space_id: spaceId,
        ...paymentFailureFromError(e)
      } : undefined;
      const failureDialog = failure ? createPaymentFailureDialog(dialog, failure, e) : dialog;
      console.error(type, message);
      if (type === 'ZEP_API_ERROR') {
        await paymentRetryDialog.show({
          errorCode: message,
          paymentFailure: isPaymentCancellation(e) ? undefined : failure,
          onRetry: () => {
            return registerPaymentCard();
          }
        });
        return;
      }
      await failureDialog.alert({
        title: t('subscription:paymentPayple.alertFailCardRegister.title'),
        content: type === 'PG_ERROR' ? t('subscription:SpaceDetail.changeCardModal.reason', {
          message
        }) : type === '401' ? t('subscription:payment.alertFailPay.descriptionLogOut') : message,
        confirmText: t('subscription:payment.alertFailPay.buttonConfirm'),
        useOutSideClick: false
      });
      if (type === '401') {
        moveSignIn(true).catch(console.error);
      }
    }
  };
  const subscribePlan = usePreservedCallback(async () => {
    try {
      await _subscribePlan({
        retry: async () => {
          await subscribePlan();
        }
      });
    } catch (e) {
      // 신규 구독 실패면 usePaymentAction 이 방금 등록한 카드를 삭제한다.
      // 삭제가 성공했을 때만 등록 상태를 되돌려 재제출 시 card_change 로 잘못 분류하지 않게 한다.
      if ((e as {
        cardRemoved?: boolean;
      }).cardRemoved) hasRegisteredCard.current = false;
      // usePaymentAction 이 원본 오류를 타입별로 처리(다이얼로그)한 뒤 흐름 제어용으로
      // Error('SubscriptionPlan') 을 던진다. 여기서 잡히는 건 그 신호일 뿐이라 PG 코드도
      // 원본 스택도 없다 — 여기서 수집하면 카드 거절·재시도까지 같은 이벤트로 뭉갠다.
      // 실제 수집은 원본이 살아 있는 usePaymentAction 의 UNKNOWN_ERROR 분기에서 한다.
    }
  });
  const updateSubscribePlan = () => {
    send(amplitudeData);
    subscribePlan().catch(console.error);
  };
  const enabled = (() => {
    // 업데이트 상황이면서
    if (isUpdated && !isUpdatePayment && isAgree) return true;
    //  loading 상태
    if (isSubmitLoading) return false;
    return isAgree && isDirty && Object.keys(errors).length === 0;
  })();
  useDidUpdate(() => {
    if (isRegisterCard) {
      subscribePlan().catch(console.error);
    }
  }, [isRegisterCard, subscribePlan]);
  return <DebounceClick wait={200} data-sentry-element="DebounceClick" data-sentry-component="PaymentSubmit" data-sentry-source-file="PaymentSubmit.tsx">
      {/* handleSubmit 트리거 하기위해 따로 분리했슴 */}
      <If condition={isOnlySubscribe} data-sentry-element="If" data-sentry-source-file="PaymentSubmit.tsx">
        <Then data-sentry-element="Then" data-sentry-source-file="PaymentSubmit.tsx">
          <Button isFullWidth size={'md'} disabled={!enabled} isLoading={isSubmitLoading} onClick={updateSubscribePlan} data-sentry-element="Button" data-sentry-source-file="PaymentSubmit.tsx">
            <ButtonTitle data-sentry-element="ButtonTitle" data-sentry-source-file="PaymentSubmit.tsx" />
          </Button>
        </Then>
        <Else data-sentry-element="Else" data-sentry-source-file="PaymentSubmit.tsx">
          <Button isFullWidth size={'md'} disabled={!enabled} isLoading={isSubmitLoading} onClick={handleSubmit(registerPaymentCard)} data-sentry-element="Button" data-sentry-source-file="PaymentSubmit.tsx">
            <ButtonTitle data-sentry-element="ButtonTitle" data-sentry-source-file="PaymentSubmit.tsx" />
          </Button>
        </Else>
      </If>
    </DebounceClick>;
};
const ButtonTitle = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    updateStatus
  } = useSubscriptionPaymentContext();
  const key = updateStatus.type === 'DOWN_GRADE' ? 'subscription:payment.button.change' : 'subscription:payment.button.checkout';
  return <>{t(key)}</>;
};